import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Waves, ArrowLeft, ChevronRight, Shield, Lock, Ban, Upload,
  Eye, Pencil, Trash2, Package, Info, Undo2, Hand, Bot,
  Database, Server, UserCheck, Search, HardDrive, Building2,
  Mail, Clock, Globe, AlertTriangle, ClipboardList } from "lucide-react";

const SECTIONS = [
  { id: "intro",        title: "1. Introdução e Controlador" },
  { id: "dados",        title: "2. Dados que Coletamos" },
  { id: "coleta",       title: "3. Como Coletamos os Dados" },
  { id: "finalidades",  title: "4. Finalidades e Bases Legais (LGPD)" },
  { id: "compartilhamento", title: "5. Compartilhamento de Dados" },
  { id: "transferencia", title: "6. Transferência Internacional" },
  { id: "retencao",     title: "7. Retenção e Exclusão" },
  { id: "direitos",     title: "8. Direitos do Titular (LGPD)" },
  { id: "cookies",      title: "9. Cookies e Rastreamento" },
  { id: "seguranca",    title: "10. Segurança dos Dados" },
  { id: "criancas",     title: "11. Privacidade de Menores" },
  { id: "terceiros",    title: "12. Links e Serviços de Terceiros" },
  { id: "alteracoes",   title: "13. Alterações nesta Política" },
  { id: "encarregado",  title: "14. Encarregado de Dados (DPO)" },
  { id: "legislacao",   title: "15. Legislação e Foro" },
];

function Tag({ color, children }) {
  const colors = {
    blue:   "bg-blue-50 text-blue-700 border-blue-200",
    green:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:  "bg-amber-50 text-amber-700 border-amber-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${colors[color]}`}>
      {children}
    </span>
  );
}

export default function PoliticaPrivacidadePage() {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Política de Privacidade — Pipe4.me"; }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/brand/icon-1.png" alt="Pipe4.me" className="w-8 h-8 object-contain" />
            </div>
            <span className="text-lg font-bold text-slate-900 font-heading">Pipe4.me</span>
          </div>
          <button onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#3B82F6] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Voltar ao início
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-10">

          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Sumário</p>
              <nav className="space-y-1">
                {SECTIONS.map(s => (
                  <a key={s.id} href={`#${s.id}`}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-slate-600 hover:text-[#3B82F6] hover:bg-blue-50 transition-colors group">
                    <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-[#3B82F6] flex-shrink-0" />
                    {s.title}
                  </a>
                ))}
              </nav>
              <div className="mt-5 pt-4 border-t border-slate-100">
                <a href="/termos-de-uso" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#3B82F6] transition-colors">
                  <ChevronRight className="w-3 h-3" /> Termos de Uso
                </a>
              </div>
            </div>
          </aside>

          {/* Conteúdo */}
          <main className="flex-1 min-w-0">
            {/* Cabeçalho */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12 mb-8">
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                <span>Pipe4.me</span><ChevronRight className="w-3 h-3" /><span>Legal</span><ChevronRight className="w-3 h-3" /><span className="text-slate-600 font-medium">Política de Privacidade</span>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-3">Política de Privacidade</h1>
                  <p className="text-slate-500 text-sm">
                    Última atualização: <strong>25 de março de 2026</strong> · Versão 1.0
                  </p>
                </div>
              </div>
              <div className="mt-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-800 leading-relaxed">
                <strong>Seu dado, sua privacidade.</strong> Esta Política descreve como o Pipe4 coleta, usa, armazena e protege seus dados pessoais, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong> e demais normas aplicáveis.
              </div>

              {/* Destaques rápidos */}
              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                {[
                  { Icon: Lock,   title: "Dados criptografados", desc: "Transmissão via TLS/HTTPS. Armazenamento seguro." },
                  { Icon: Ban,    title: "Não vendemos seus dados", desc: "Jamais comercializamos dados pessoais a terceiros." },
                  { Icon: Upload, title: "Portabilidade garantida", desc: "Exporte seus dados a qualquer momento." },
                ].map(({ Icon, title, desc }, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <Icon className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs font-semibold text-slate-800">{title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12 space-y-12 text-slate-700 leading-relaxed">

              {/* ── 1. Introdução ── */}
              <section id="intro">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">1. Introdução e Controlador dos Dados</h2>
                <p className="mb-3">A <strong>Satellitus Tecnologia S.A.</strong>, detentora da marca <strong>Pipe4.me</strong> ("Pipe4", "nós", "nosso"), pessoa jurídica constituída segundo as leis brasileiras, é a <strong>controladora</strong> dos dados pessoais tratados no âmbito da plataforma Pipe4.me — CRM SaaS para equipes de vendas.</p>
                <p className="mb-3">Esta Política de Privacidade ("Política") se aplica a todos os usuários da Plataforma, visitantes do site institucional, bem como às pessoas cujos dados sejam inseridos na Plataforma pelos nossos clientes (e.g., contatos e leads cadastrados por usuários do sistema).</p>
                <p className="mb-3">Ao utilizar a Plataforma ou fornecer dados à Pipe4, o titular confirma ter lido e compreendido esta Política. Recomendamos a leitura periódica, pois pode ser atualizada.</p>
                <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm">
                  <p className="font-semibold text-slate-700 mb-1">Identificação do Controlador:</p>
                  <p>Satellitus Tecnologia S.A. · CNPJ: [a ser cadastrado]</p>
                  <p>E-mail do DPO: <a href="mailto:legal@pipe4.me" className="text-[#3B82F6] hover:underline">legal@pipe4.me</a></p>
                </div>
              </section>

              {/* ── 2. Dados Coletados ── */}
              <section id="dados">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">2. Dados que Coletamos</h2>
                <p className="mb-5">Coletamos diferentes categorias de dados, de acordo com a relação estabelecida com o titular:</p>

                <div className="space-y-5">
                  {[
                    {
                      tag: "Dados de Cadastro", color: "blue",
                      items: [
                        "Nome completo e endereço de e-mail",
                        "Foto de perfil (quando fornecida pelo usuario)",
                        "Informações da organização (nome do tenant, segmento)",
                        "Cargo / função dentro da organização",
                      ]
                    },
                    {
                      tag: "Dados de Uso da Plataforma", color: "purple",
                      items: [
                        "Registros de acesso (IP, data/hora, dispositivo, navegador)",
                        "Ações realizadas na Plataforma (criação, edição, exclusão de registros — log de auditoria)",
                        "Preferências de configuração (idioma, fuso horário, tema)",
                        "Sessões de uso e métricas de engajamento (para melhoria do produto)",
                      ]
                    },
                    {
                      tag: "Dados Inseridos pelo Usuário", color: "amber",
                      items: [
                        "Informações de negócios (deals): título, valor, etapa, responsável, campos personalizados",
                        "Dados de contatos e leads: nome, e-mail, telefone, empresa, endereço",
                        "Dados de empresas: razão social, CNPJ, segmento, localização",
                        "Produtos e serviços cadastrados pelo cliente",
                        "Mensagens de WhatsApp trocadas pela integração (quando habilitada)",
                        "Arquivos e documentos importados via planilhas Excel/CSV",
                      ]
                    },
                    {
                      tag: "Dados de Pagamento", color: "green",
                      items: [
                        "Dados de faturamento (nome do responsável, CPF/CNPJ, endereço de cobrança)",
                        "Dados de pagamento são processados por parceiros de pagamento certificados (PCI-DSS) — a Pipe4 não armazena números de cartão integralmente",
                        "Histórico de transações e faturas",
                      ]
                    },
                  ].map((cat, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                        <Tag color={cat.color}>{cat.tag}</Tag>
                      </div>
                      <ul className="divide-y divide-slate-100">
                        {cat.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-2 px-4 py-2.5 text-sm">
                            <span className="text-slate-300 flex-shrink-0 mt-0.5">–</span>{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <p className="mt-4 text-sm text-slate-500">A Pipe4 <strong>não coleta intencionalmente</strong> dados considerados sensíveis pela LGPD (origem racial, convicções religiosas, dados de saúde, biometria, etc.). Caso tais dados sejam inseridos pelo usuário em campos livres, sua proteção passa a ser responsabilidade do usuário, que deve garantir a devida base legal para tal tratamento.</p>
              </section>

              {/* ── 3. Como Coletamos ── */}
              <section id="coleta">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">3. Como Coletamos os Dados</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: "Diretamente do usuário", desc: "Ao criar conta, preencher formulários, importar dados, configurar a Plataforma ou entrar em contato com nosso suporte." },
                    { title: "Automaticamente", desc: "Por meio de cookies, logs de servidor e tecnologias similares ao navegar na Plataforma, coletamos dados técnicos de acesso e uso." },
                    { title: "Autenticacao por e-mail e senha", desc: "Ao criar conta ou fazer login, coletamos nome, e-mail e senha (armazenada com hash seguro). Dados de recuperacao de senha sao processados via e-mail transacional." },
                    { title: "Integrações de terceiros", desc: "Quando o usuário habilita integrações (WhatsApp, e-mail SMTP), dados de comunicação podem ser processados pela Plataforma mediante autorização expressa." },
                    { title: "Formulários públicos", desc: "Dados inseridos por visitantes em formulários públicos criados por nossos clientes são coletados em nome do cliente, que é responsável por obter o consentimento adequado." },
                    { title: "Importação de dados", desc: "Ao importar planilhas Excel/CSV, o usuário insere na Plataforma dados de seus contatos e clientes, sendo responsável pela legalidade de tal importação." },
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-sm font-semibold text-slate-800 mb-1">{item.title}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 4. Finalidades e Bases Legais ── */}
              <section id="finalidades">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">4. Finalidades e Bases Legais (LGPD)</h2>
                <p className="mb-5 text-sm">Tratamos seus dados com fundamento nas bases legais previstas no art. 7º da LGPD. A tabela abaixo detalha as finalidades e as respectivas bases legais:</p>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wide">Finalidade</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wide">Base Legal (LGPD)</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wide">Dados Envolvidos</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        ["Criação e manutenção da conta", "Execução de contrato (art. 7º, V)", "Cadastro, autenticação"],
                        ["Prestação dos serviços contratados", "Execução de contrato (art. 7º, V)", "Todos os dados inseridos na Plataforma"],
                        ["Cobrança e faturamento", "Execução de contrato (art. 7º, V)", "Dados de pagamento e faturamento"],
                        ["Comunicações operacionais (atualizações, manutenções, alertas)", "Execução de contrato (art. 7º, V)", "E-mail cadastrado"],
                        ["Cumprimento de obrigações legais e regulatórias", "Obrigação legal (art. 7º, II)", "Dados fiscais e de acesso"],
                        ["Segurança da informação e prevenção a fraudes", "Interesse legítimo (art. 7º, IX)", "Logs de acesso, IPs"],
                        ["Melhoria do produto e análise de uso", "Interesse legítimo (art. 7º, IX)", "Dados de uso anonimizados"],
                        ["Marketing e comunicações promocionais", "Consentimento (art. 7º, I)", "E-mail, nome"],
                        ["Suporte ao cliente e resolução de problemas", "Execução de contrato (art. 7º, V)", "Dados relevantes ao chamado"],
                        ["Defesa em processos judiciais ou administrativos", "Exercício regular de direitos (art. 7º, VI)", "Dados necessários à defesa"],
                      ].map(([fin, base, dados], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                          <td className="px-4 py-3 text-slate-700">{fin}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{base}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{dados}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm text-slate-500">O usuário que não deseje receber comunicações de marketing pode cancelar a qualquer momento pelo link de descadastro presente nos e-mails ou por solicitação ao DPO.</p>
              </section>

              {/* ── 5. Compartilhamento ── */}
              <section id="compartilhamento">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">5. Compartilhamento de Dados</h2>
                <p className="mb-4"><strong>A Pipe4 não vende, aluga ou comercializa dados pessoais a terceiros.</strong> Compartilhamos dados apenas nas seguintes hipóteses:</p>
                <div className="space-y-3">
                  {[
                    {
                      title: "Fornecedores e Parceiros de Serviço",
                      desc: "Empresas que nos auxiliam na prestação dos serviços: infraestrutura em nuvem (hospedagem e banco de dados), processadores de pagamento, serviços de e-mail transacional e monitoramento de sistema. Todos estão contratualmente obrigados a proteger os dados e utilizá-los apenas para as finalidades autorizadas.",
                    },
                    {
                      title: "Autoridades Governamentais",
                      desc: "Quando exigido por lei, ordem judicial, autoridade fiscal, regulatória ou policial competente. Nestes casos, divulgamos apenas os dados estritamente necessários e, quando legalmente permitido, notificamos o titular.",
                    },
                    {
                      title: "Proteção de Direitos",
                      desc: "Em processos judiciais ou arbitrais para defesa de direitos da Pipe4 ou de terceiros, incluindo investigação de fraude, violação de segurança ou violações destes Termos.",
                    },
                    {
                      title: "Transferência Societária",
                      desc: "Em caso de fusão, aquisição, venda de ativos ou reorganização societária, os dados poderão ser transferidos ao sucessor, que ficará vinculado a esta Política. Os usuários serão notificados com antecedência.",
                    },
                    {
                      title: "Com Consentimento do Titular",
                      desc: "Em quaisquer outras situações não previstas acima, somente com seu consentimento prévio, expresso e específico.",
                    },
                  ].map((item, i) => (
                    <div key={i} className="p-4 border border-slate-200 rounded-xl">
                      <p className="text-sm font-semibold text-slate-800 mb-1.5">{item.title}</p>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── 6. Transferência Internacional ── */}
              <section id="transferencia">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">6. Transferência Internacional de Dados</h2>
                <p className="mb-3">A infraestrutura da Pipe4 é operada em servidores localizados no Brasil e/ou em outros países com nível de proteção de dados adequado. Quando dados pessoais são transferidos para fora do Brasil, observamos os requisitos do art. 33 da LGPD, adotando uma ou mais das seguintes garantias:</p>
                <ul className="space-y-2 text-sm mb-4">
                  {[
                    "Transferência para países com grau de proteção de dados considerado adequado pela ANPD",
                    "Cláusulas contratuais padrão aprovadas pela ANPD",
                    "Certificações e selos de privacidade reconhecidos",
                    "Normas corporativas globais (binding corporate rules)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#3B82F6] flex-shrink-0">•</span>{item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm">Atualmente, utilizamos serviços de infraestrutura de fornecedores globais que mantêm certificações de segurança e privacidade (ISO 27001, SOC 2) e cumprem as exigências do GDPR europeu, conferindo nível de proteção compatível com a LGPD.</p>
              </section>

              {/* ── 7. Retenção ── */}
              <section id="retencao">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">7. Retenção e Exclusão de Dados</h2>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Categoria de Dado</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Prazo de Retenção</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Fundamento</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        ["Dados da conta ativa", "Enquanto a conta estiver ativa", "Execução de contrato"],
                        ["Dados após cancelamento", "30 dias (disponíveis para exportação) + 60 dias em backup", "Proteção do titular"],
                        ["Logs de acesso e auditoria", "6 meses (art. 15 do Marco Civil da Internet)", "Obrigação legal"],
                        ["Dados de pagamento e fiscais", "5 anos", "Obrigação tributária"],
                        ["E-mails de suporte", "2 anos após resolução", "Interesse legítimo"],
                        ["Dados de marketing (com consentimento)", "Até revogação do consentimento", "Consentimento"],
                        ["Logs de segurança (tentativas de acesso)", "1 ano", "Interesse legítimo / segurança"],
                      ].map(([cat, prazo, fund], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                          <td className="px-4 py-3 text-slate-700">{cat}</td>
                          <td className="px-4 py-3 text-slate-600 font-medium text-xs">{prazo}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{fund}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm text-slate-500">Após o término dos prazos, os dados são excluídos de forma segura e irreversível ou anonimizados, impossibilitando a identificação do titular.</p>
              </section>

              {/* ── 8. Direitos do Titular ── */}
              <section id="direitos">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">8. Direitos do Titular (LGPD — art. 18)</h2>
                <p className="mb-5 text-sm">Nos termos da LGPD, você possui os seguintes direitos em relação aos seus dados pessoais, exercíveis a qualquer momento mediante solicitação ao DPO:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { Icon: Eye,      title: "Acesso",              desc: "Saber quais dados pessoais seus tratamos, de que forma, por quê e com quem os compartilhamos." },
                    { Icon: Pencil,   title: "Correção",             desc: "Solicitar a correção de dados incompletos, inexatos ou desatualizados." },
                    { Icon: Trash2,   title: "Eliminação",           desc: "Solicitar a exclusão dos dados tratados com base em consentimento ou quando desnecessários, excessivos ou tratados em desconformidade com a LGPD." },
                    { Icon: Lock,     title: "Anonimização ou Bloqueio", desc: "Solicitar anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos." },
                    { Icon: Package,  title: "Portabilidade",        desc: "Receber seus dados em formato estruturado e interoperável para transferência a outro fornecedor de serviço ou produto." },
                    { Icon: Info,     title: "Informação sobre Compartilhamento", desc: "Saber com quais entidades públicas e privadas a Pipe4 compartilhou seus dados." },
                    { Icon: Ban,      title: "Não Fornecimento de Consentimento", desc: "Ser informado sobre a possibilidade de não fornecer consentimento e as consequências da negativa." },
                    { Icon: Undo2,    title: "Revogação do Consentimento", desc: "Revogar o consentimento dado anteriormente, quando o tratamento se basear nessa hipótese." },
                    { Icon: Hand,     title: "Oposição",             desc: "Opor-se ao tratamento de dados realizado com fundamento em hipóteses diversas do consentimento, em caso de descumprimento da LGPD." },
                    { Icon: Bot,      title: "Revisão de Decisões Automatizadas", desc: "Solicitar revisão de decisões tomadas unicamente com base em tratamento automatizado que afetem seus interesses." },
                  ].map(({ Icon, title, desc }, i) => (
                    <div key={i} className="flex gap-3 p-3.5 border border-slate-200 rounded-xl hover:border-[#3B82F6]/30 hover:bg-blue-50/30 transition-colors">
                      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700">
                  <strong>Como exercer seus direitos:</strong> Envie solicitação ao nosso DPO pelo e-mail <a href="mailto:legal@pipe4.me" className="underline">legal@pipe4.me</a>, identificando-se e descrevendo o direito que deseja exercer. Responderemos em até <strong>15 (quinze) dias úteis</strong>, podendo ser prorrogado por mais 15 dias em casos complexos, com comunicação prévia. O exercício dos direitos é gratuito.
                </div>
                <p className="mt-3 text-xs text-slate-500">O titular também pode apresentar petição à <strong>Autoridade Nacional de Proteção de Dados (ANPD)</strong> caso considere que seus direitos não foram atendidos adequadamente.</p>
              </section>

              {/* ── 9. Cookies ── */}
              <section id="cookies">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">9. Cookies e Tecnologias de Rastreamento</h2>
                <p className="mb-4 text-sm">Utilizamos cookies e tecnologias similares para funcionamento da Plataforma, autenticação, preferências do usuário e análise de uso. Os tipos de cookies utilizados são:</p>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Finalidade</th>
                        <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase">Duração</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        ["Estritamente necessários", "Autenticação (session_token), preferências de sessão", "Sessão / 7 dias", "Não"],
                        ["Funcionais", "Idioma selecionado, pipeline ativo, preferências de layout", "Persistente", "Não (funcionalidade essencial)"],
                        ["Analíticos", "Métricas de uso e performance da Plataforma (dados agregados e anônimos)", "Até 2 anos", "Opt-out disponível"],
                        ["LocalStorage", "Mapeamentos de importação salvo localmente, preferências de UI", "Persistente", "Não (funcionalidade do usuário)"],
                      ].map(([tipo, fin, dur, consent], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                          <td className="px-4 py-3 font-medium text-slate-700">{tipo}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{fin}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{dur}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-sm text-slate-500">Você pode gerenciar ou desativar cookies nas configurações do seu navegador, mas isso pode impactar o funcionamento de algumas funcionalidades da Plataforma, especialmente a autenticação.</p>
              </section>

              {/* ── 10. Segurança ── */}
              <section id="seguranca">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">10. Segurança dos Dados</h2>
                <p className="mb-4 text-sm">Adotamos medidas técnicas e organizacionais proporcionais ao risco para proteger os dados pessoais contra acesso não autorizado, uso indevido, alteração, divulgação ou destruição:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { Icon: Lock,       title: "Criptografia em trânsito", desc: "Todas as comunicações entre cliente e servidor utilizam TLS 1.2 ou superior (HTTPS)." },
                    { Icon: Building2,  title: "Isolamento por tenant",    desc: "Dados de cada organização são logicamente isolados. Nenhum tenant acessa dados de outro." },
                    { Icon: UserCheck,  title: "Controle de acesso",       desc: "Princípio do menor privilégio: cada usuário acessa apenas o necessário, conforme perfil de permissão." },
                    { Icon: ClipboardList, title: "Log de auditoria",      desc: "Todas as ações relevantes são registradas com usuário, data, hora e dados antes/depois da alteração." },
                    { Icon: HardDrive,  title: "Backups",                  desc: "Backups automáticos periódicos com criptografia, permitindo recuperação em caso de incidente." },
                    { Icon: Search,     title: "Monitoramento contínuo",   desc: "Monitoramento 24/7 de anomalias, tentativas de acesso indevido e vulnerabilidades." },
                  ].map(({ Icon, title, desc }, i) => (
                    <div key={i} className="flex gap-3 p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="text-xs font-semibold text-slate-800">{title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
                  <strong>Incidentes de Segurança:</strong> Em caso de incidente que comprometa dados pessoais, notificaremos a ANPD e os titulares afetados nos prazos legais (art. 48 da LGPD), descrevendo a natureza do incidente, dados envolvidos, riscos e medidas adotadas.
                </div>
              </section>

              {/* ── 11. Crianças ── */}
              <section id="criancas">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">11. Privacidade de Menores de Idade</h2>
                <p className="mb-3 text-sm">A Plataforma Pipe4 é destinada exclusivamente a pessoas com capacidade civil plena, maiores de 18 (dezoito) anos, para uso empresarial. <strong>Não coletamos intencionalmente dados de menores de 18 anos.</strong></p>
                <p className="text-sm">Caso identificarmos que dados de menores foram coletados inadvertidamente, tomaremos as medidas necessárias para excluí-los imediatamente. Se você acredita que coletamos dados de um menor, entre em contato com nosso DPO imediatamente.</p>
              </section>

              {/* ── 12. Terceiros ── */}
              <section id="terceiros">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">12. Links e Serviços de Terceiros</h2>
                <p className="mb-3 text-sm">A Plataforma pode conter integrações ou links para serviços de terceiros (ex.: WhatsApp/Meta, Google, provedores de e-mail SMTP). Esta Política se aplica exclusivamente aos dados tratados pela Pipe4. Recomendamos a leitura das políticas de privacidade dos serviços de terceiros utilizados em conjunto com a Plataforma.</p>
                <p className="text-sm">A Pipe4 não se responsabiliza pelas práticas de privacidade de terceiros, mesmo que integrados à Plataforma. As integrações são opcionais e requerem configuração e habilitação explícita pelo usuário.</p>
              </section>

              {/* ── 13. Alterações ── */}
              <section id="alteracoes">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">13. Alterações nesta Política</h2>
                <p className="mb-3 text-sm">Esta Política pode ser atualizada periodicamente para refletir mudanças nos serviços, na legislação ou nas práticas de privacidade. Toda alteração relevante será comunicada por:</p>
                <ul className="space-y-1 text-sm mb-4">
                  {["E-mail enviado ao endereço cadastrado", "Notificação destacada na Plataforma", "Publicação da versão atualizada nesta página"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-[#3B82F6]">•</span>{item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm">A data de "última atualização" no topo desta página reflete a versão vigente. O uso continuado da Plataforma após a entrada em vigor das alterações implica concordância com a nova Política. Versões anteriores podem ser solicitadas ao DPO.</p>
              </section>

              {/* ── 14. DPO ── */}
              <section id="encarregado">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">14. Encarregado de Proteção de Dados (DPO)</h2>
                <p className="mb-4 text-sm">Nos termos do art. 41 da LGPD, a Pipe4 designou um Encarregado de Proteção de Dados (DPO — Data Protection Officer) responsável por:</p>
                <ul className="space-y-1 text-sm mb-5">
                  {[
                    "Aceitar reclamações e comunicações dos titulares e da ANPD",
                    "Orientar os colaboradores da Empresa sobre práticas de proteção de dados",
                    "Executar as demais atribuições determinadas pelo controlador ou estabelecidas em normas complementares",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#3B82F6] flex-shrink-0">•</span>{item}
                    </li>
                  ))}
                </ul>
                <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="font-semibold text-slate-800 mb-3">Contato do Encarregado (DPO):</p>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> <a href="mailto:legal@pipe4.me" className="text-[#3B82F6] hover:underline">legal@pipe4.me</a></p>
                    <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /> Prazo de resposta: até 15 dias úteis</p>
                    <p className="flex items-center gap-2"><Info className="w-3.5 h-3.5 text-slate-400" /> Identifique-se no contato e descreva claramente sua solicitação</p>
                  </div>
                </div>
              </section>

              {/* ── 15. Legislação ── */}
              <section id="legislacao">
                <h2 className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">15. Legislação Aplicável e Foro</h2>
                <p className="mb-3 text-sm">Esta Política é regida pelas leis da República Federativa do Brasil, especialmente:</p>
                <ul className="space-y-1.5 text-sm mb-4">
                  {[
                    "Lei nº 13.709/2018 — Lei Geral de Proteção de Dados (LGPD)",
                    "Lei nº 12.965/2014 — Marco Civil da Internet",
                    "Lei nº 8.078/1990 — Código de Defesa do Consumidor",
                    "Decreto nº 8.771/2016 — Regulamentação do Marco Civil da Internet",
                    "Resoluções e regulamentações da Autoridade Nacional de Proteção de Dados (ANPD)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#3B82F6] flex-shrink-0">•</span>{item}
                    </li>
                  ))}
                </ul>
                <p className="text-sm">Fica eleito o foro da Comarca de <strong>São Paulo/SP</strong> para dirimir quaisquer controvérsias decorrentes desta Política, sem prejuízo do direito do titular de acionar a ANPD ou o Procon competente.</p>
              </section>

              {/* Rodapé do documento */}
              <div className="pt-8 mt-8 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center overflow-hidden">
                    <img src="/brand/icon-1.png" alt="Pipe4.me" className="w-6 h-6 object-contain" />
                  </div>
                  <span className="text-sm font-bold text-slate-700">Satellitus Tecnologia S.A.</span>
                </div>
                <p className="text-xs text-slate-400">Última atualização: 25 de março de 2026 · Versão 1.0</p>
                <p className="text-xs text-slate-400 mt-1">
                  Veja também nossos{" "}
                  <a href="/termos-de-uso" className="text-[#3B82F6] hover:underline">Termos de Uso</a>
                  {" "}· Dúvidas:{" "}
                  <a href="mailto:legal@pipe4.me" className="text-[#3B82F6] hover:underline">legal@pipe4.me</a>
                </p>
              </div>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
